/**
 * TableCurate · standalone Magic Curation studio.
 *
 * Founder direction (TABLE_MAGIC_CURATION.md §5 · 4 Jun 2026):
 *   "Where it lives — a standalone, login-gated studio at
 *   /table/curate. Not a member tab, not a toggle on search. /table
 *   stays the member's clean discovery surface; this is the
 *   concierge's generation workshop."
 *
 * UX (concierge-first; member self-serve later by permission):
 *   1. Concierge® / admin auth gate (same console session)
 *   2. Inline venue picker — searches /api/table/search live and
 *      multi-selects verified cards
 *   3. Brief input — plain-language English
 *   4. Compose → /api/table/curate
 *   5. Draft review with verified-slots rendered inline
 *   6. Founder approval gate (required before any external send)
 *
 * Anti-fab guardrails (visible to the concierge):
 *   - "composing on verified slate · reading not recalling" microcopy
 *   - Picks are dropped client-side if `venue_id` doesn't match an
 *     id we picked (mirror of the backend post-pass)
 *   - "no external send until approved" banner under the draft
 */
import React, { useCallback, useEffect, useState } from "react";
import { canonicalVenueId } from "@/lib/venue-id";
import { useNavigate } from "react-router-dom";
import { useConsoleAuth } from "@/lib/console-auth";
import {
    searchTable, composeCuration, approveCuration,
    dossierUrl, dossierDocxUrl,
    fetchCurationWhatsApp, fetchCurationCollection,
    sendCurationWhatsApp,
    parseCurationBrief, discoverFromBrief,
    discoverMoreForDraft, appendPickToDraft,
    getVenueDetail,
} from "@/lib/table-api";
import { AskConciergePill } from "@/components/table/AskConciergePill";
import { RestaurantDrawer } from "@/components/table/RestaurantDrawer";

const PAGE_BG = "var(--t-paper)";

export default function TableCurate() {
    // Founder direction 9 Jun 2026 PM late · "Curate missing — let it
    // be open to all." The studio is no longer console-gated · any
    // visitor can compose a brief, browse the slate, and review the
    // dossier. (Server-side endpoints remain free-to-call for the
    // discovery/compose path; the desk-only Approve & Send action is
    // already separately auth-checked at the backend.)
    const { user } = useConsoleAuth();
    return <Studio user={user || null} />;
}


function Studio({ user }) {
    // Magic Curation Phase B4 (founder ruling 5 Jun 2026 night):
    // brief-driven is the default front door · the existing pick-first
    // flow stays behind a "manual mode" toggle for the case where the
    // founder already knows the venues (a regular client's three
    // favourites she wants to dossier without re-discovery).
    const [mode, setMode] = useState("brief");   // "brief" | "manual"

    // Venue picker · live search against the verified pipeline (manual mode)
    const [pickerQ, setPickerQ] = useState("");
    const [pickerResults, setPickerResults] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerSearched, setPickerSearched] = useState(false);  // true after first run
    const [pickerLastQ, setPickerLastQ] = useState("");
    const [picks, setPicks] = useState([]); // selected verified venues
    // Detail drawer · founder ask 11 Feb 2026 PM · "popup with the
    // venue's full detail as each one is selected, so the curator
    // can read about it before adding it." REUSES the existing
    // `RestaurantDrawer` so the curator reads the same verified,
    // sourced detail a member sees — never a thinner copy.
    const [drawerVenue, setDrawerVenue] = useState(null);
    // Loading flag while we re-resolve the slate card into a full
    // venue detail record (the slate carries the card shape; the
    // drawer wants the get_venue_detail shape with cuisine_tags,
    // hours, nearby_alternatives etc.).
    const [drawerLoading, setDrawerLoading] = useState(false);
    // Brief
    const [brief, setBrief] = useState("");
    const [occasion, setOccasion] = useState("");
    const [locale, setLocale] = useState("");
    // Draft state machine
    const [stage, setStage] = useState("compose"); // compose · composing · draft · error
    const [draft, setDraft] = useState(null);
    const [error, setError] = useState(null);
    const [approving, setApproving] = useState(false);

    const runPickerSearch = useCallback(async () => {
        if (pickerQ.trim().length < 2) return;
        setPickerLoading(true);
        setPickerSearched(true);
        setPickerLastQ(pickerQ.trim());
        try {
            const data = await searchTable(pickerQ.trim(), { limit: 8 });
            setPickerResults(data.results || []);
        } catch (e) {
            setPickerResults([]);
        } finally {
            setPickerLoading(false);
        }
    }, [pickerQ]);

    // ── Auto-search · debounce 350ms while typing (founder fix
    // 5 Jun 2026 morning · "studio search seems dead" was UX:
    // the picker required an explicit Enter / Search-click. We now
    // fire as soon as the query is ≥ 3 chars so results appear live).
    // The explicit Search button stays as an obvious affordance and
    // forces an immediate re-fetch.
    useEffect(() => {
        const q = pickerQ.trim();
        if (q.length < 3) return;
        if (q === pickerLastQ && pickerSearched) return;
        const t = setTimeout(() => { runPickerSearch(); }, 350);
        return () => clearTimeout(t);
    }, [pickerQ, runPickerSearch, pickerLastQ, pickerSearched]);

    const togglePick = (v) => {
        setPicks((cur) => {
            if (cur.some((x) => x.id === v.id)) return cur.filter((x) => x.id !== v.id);
            if (cur.length >= 12) return cur;
            return [...cur, v];
        });
    };

    // Open the existing RestaurantDrawer with the FULL verified
    // detail record · founder ask 11 Feb 2026 PM. The slate card
    // carries the lightweight card shape; we re-resolve via
    // getVenueDetail so the drawer renders the same photos /
    // hours / nearby / cuisine_tags / atmosphere a member sees on
    // /table. Falls back to the slate-card shape if the detail
    // re-resolve fails (never blocks the curator).
    const openDrawer = useCallback(async (v) => {
        if (!v) return;
        const venueId = canonicalVenueId(v);
        if (!venueId) {
            setDrawerVenue(v);
            return;
        }
        setDrawerLoading(true);
        setDrawerVenue(v);   // optimistic · shows the lightweight card immediately
        try {
            const detail = await getVenueDetail(venueId);
            if (detail && (detail.id || detail.place_id)) {
                setDrawerVenue(detail);
            }
        } catch (e) {
            // Honest fallback · keep the lightweight card on screen
            // rather than crashing the drawer. The curator still
            // sees name/city/rating/cuisine from the slate shape.
        } finally {
            setDrawerLoading(false);
        }
    }, []);

    const closeDrawer = useCallback(() => {
        setDrawerVenue(null);
        setDrawerLoading(false);
    }, []);

    const removePick = (id) =>
        setPicks((cur) => cur.filter((x) => x.id !== id));

    const compose = async (explicitPicks, explicitBrief, explicitOccasion) => {
        // Accept explicit overrides so the brief-driven flow can fire
        // compose immediately after setting state without waiting for
        // React to flush the useState batch. Defensive shape check
        // because manual mode wires this as a click handler and React
        // would otherwise pass the synthetic event as `explicitPicks`.
        const effectivePicks = Array.isArray(explicitPicks) ? explicitPicks : picks;
        const effectiveBrief = (typeof explicitBrief === "string" ? explicitBrief : brief).trim();
        const effectiveOccasion = (typeof explicitOccasion === "string" ? explicitOccasion : occasion).trim();
        if (effectiveBrief.length < 4 || effectivePicks.length === 0) return;
        setStage("composing");
        setError(null);
        try {
            const data = await composeCuration({
                brief: effectiveBrief,
                venue_ids: effectivePicks.map((v) => v.id).filter(Boolean),
                occasion: effectiveOccasion || null,
                locale: locale.trim() || null,
            });
            setDraft(data);
            setStage("draft");
        } catch (e) {
            setError(e?.body?.detail?.message || e?.body || e?.message || "Curation failed");
            setStage("error");
        }
    };

    const approve = async () => {
        if (!draft?.draft_id || draft.approved) return;
        setApproving(true);
        try {
            const updated = await approveCuration(draft.draft_id);
            setDraft(updated);
        } catch (e) {
            setError(e?.body || e?.message || "Approval failed");
        } finally {
            setApproving(false);
        }
    };

    const reset = () => {
        setStage("compose");
        setDraft(null);
        setError(null);
        setPicks([]);
        setBrief("");
    };

    return (
        <main
            data-table-surface
            data-testid="table-curate-studio"
            style={{ minHeight: "100vh", background: PAGE_BG, color: "var(--t-ink)" }}
        >
            <Header userName={user?.name || user?.email} />

            <div
                className="mx-auto px-6 py-10"
                style={{ maxWidth: 1100 }}
            >
                {/* Phase B4 · brief-driven default; pick-first as
                    "manual mode" toggle (founder ruling 5 Jun 2026
                    night: demote, don't delete). */}
                {stage === "compose" || stage === "composing" ? (
                    <ModeToggle mode={mode} setMode={setMode} />
                ) : null}

                {(stage === "compose" || stage === "composing") && mode === "brief" ? (
                    <BriefDrivenView
                        onOpenDetail={openDrawer}
                        onComposed={(slate, briefText, occasionText) => {
                            // Push the discovered slate + brief into the
                            // existing compose path · no re-discovery
                            // happens · slate is verified pipeline output.
                            // Normalise `.id` so each pick survives the
                            // compose endpoint's `picks.map(v => v.id)`
                            // contract even if the venue only carries
                            // `place_id`.
                            const normalised = (slate || []).map((v) => ({
                                ...v,
                                id: v.id || v.place_id,
                            }));
                            setPicks(normalised);
                            setBrief(briefText);
                            setOccasion(occasionText || "");
                            // Fire compose immediately. We pass the
                            // normalised slate directly to avoid the
                            // useState batching trap (compose() would
                            // otherwise read the previous picks).
                            setTimeout(() => compose(normalised, briefText, occasionText), 50);
                        }}
                        composing={stage === "composing"}
                    />
                ) : null}

                {(stage === "compose" || stage === "composing") && mode === "manual" ? (
                    <ComposeView
                        pickerQ={pickerQ} setPickerQ={setPickerQ}
                        pickerResults={pickerResults}
                        pickerLoading={pickerLoading}
                        pickerSearched={pickerSearched}
                        pickerLastQ={pickerLastQ}
                        runPickerSearch={runPickerSearch}
                        picks={picks}
                        togglePick={togglePick}
                        removePick={removePick}
                        onOpenDetail={openDrawer}
                        brief={brief} setBrief={setBrief}
                        occasion={occasion} setOccasion={setOccasion}
                        locale={locale} setLocale={setLocale}
                        compose={compose}
                        composing={stage === "composing"}
                    />
                ) : null}

                {stage === "draft" && draft ? (
                    <DraftView
                        draft={draft}
                        onApprove={approve}
                        approving={approving}
                        onNew={reset}
                        onUpdate={(updatedDraft) => setDraft(updatedDraft)}
                    />
                ) : null}

                {stage === "error" && error ? (
                    <ErrorPanel error={error} onRetry={() => setStage("compose")} />
                ) : null}
            </div>

            {/* Detail drawer · founder ask 11 Feb 2026 PM ·
                REUSES the same `RestaurantDrawer` members see on
                /table. The curator reads the verified, sourced
                detail before adding the pick — never a thinner
                copy. The drawer self-mounts as a fixed overlay
                so it sits above the studio body when open. */}
            {drawerVenue ? (
                <RestaurantDrawer
                    venue={drawerVenue}
                    onClose={closeDrawer}
                    onReserve={undefined}
                    onConciergeOverride={undefined}
                    onSelectNearby={undefined}
                />
            ) : null}
        </main>
    );
}


function Header({ userName }) {
    return (
        <header
            className="px-6 py-5 flex items-center justify-between"
            style={{
                borderBottom: "1px solid var(--t-line)",
                background: "var(--t-paper2)",
            }}
        >
            <div className="flex items-center gap-3">
                <span
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.24em",
                        color: "var(--t-brass)", fontWeight: 700,
                    }}
                >
                    ✦ Magic Curation · Concierge® Studio
                </span>
                <span
                    className="t-mono"
                    style={{ fontSize: 9, letterSpacing: "0.18em", color: "var(--t-ink3)" }}
                >
                    · grounded compose · pipeline owns the truth
                </span>
            </div>
            <div className="flex items-center gap-4">
                <a
                    href="/table/admin"
                    data-testid="curate-link-to-admin"
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.18em",
                        color: "var(--t-ink2)", textDecoration: "none",
                        borderBottom: "1px solid var(--t-line2)",
                        paddingBottom: 2,
                    }}
                >
                    observability ↗
                </a>
                {userName ? (
                    <span
                        className="t-mono"
                        style={{ fontSize: 11, color: "var(--t-ink2)" }}
                        data-testid="curate-user-name"
                    >
                        {userName}
                    </span>
                ) : null}
            </div>
        </header>
    );
}


function ComposeView({
    pickerQ, setPickerQ, pickerResults, pickerLoading,
    pickerSearched, pickerLastQ, runPickerSearch,
    picks, togglePick, removePick, onOpenDetail,
    brief, setBrief, occasion, setOccasion, locale, setLocale,
    compose, composing,
}) {
    const pickedIds = new Set(picks.map((p) => p.id));
    return (
        <div className="grid lg:grid-cols-2 gap-10">
            {/* LEFT · Venue picker */}
            <section data-testid="curate-picker">
                <h2
                    className="t-serif"
                    style={{
                        fontSize: 26, fontWeight: 500,
                        lineHeight: 1.15, marginBottom: 4,
                        letterSpacing: "-0.01em",
                    }}
                >
                    1 · Verified slate
                </h2>
                <p style={{ fontSize: 13, color: "var(--t-ink3)", marginBottom: 18 }}>
                    Search live · multi-select up to 12 verified venues.
                </p>
                <div className="flex gap-2 mb-3">
                    <input
                        data-testid="curate-picker-input"
                        value={pickerQ}
                        onChange={(e) => setPickerQ(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                runPickerSearch();
                            }
                        }}
                        placeholder="Search the verified pipeline · 'Fallow London' · 'Pizza 4Ps Indiranagar' · 'Saido Tokyo'…"
                        style={pickerInputStyle}
                        disabled={pickerLoading}
                    />
                    <button
                        type="button"
                        data-testid="curate-picker-run"
                        onClick={runPickerSearch}
                        disabled={pickerLoading || pickerQ.trim().length < 2}
                        style={{
                            background: pickerLoading
                                ? "var(--t-line)"
                                : "var(--t-ink)",
                            color: "var(--t-paper2)",
                            border: 0, borderRadius: 10,
                            padding: "0 16px", fontSize: 13, fontWeight: 600,
                            cursor: pickerLoading ? "wait" : "pointer",
                            minWidth: 86,
                        }}
                    >
                        {pickerLoading ? "…" : "Search"}
                    </button>
                </div>
                <div
                    data-testid="curate-picker-results"
                    style={{
                        background: "var(--t-paper2)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 14, padding: "8px 6px",
                        minHeight: 180,
                        maxHeight: 360, overflowY: "auto",
                        marginBottom: 18,
                        position: "relative",
                    }}
                >
                    {pickerLoading ? (
                        <div
                            className="t-mono uppercase"
                            data-testid="curate-picker-loading"
                            style={{
                                padding: "32px 16px", textAlign: "center",
                                color: "var(--t-brass)",
                                fontSize: 11, letterSpacing: "0.22em",
                                fontStyle: "italic",
                            }}
                        >
                            · reading the verified pipeline…
                        </div>
                    ) : !pickerSearched ? (
                        <p
                            className="t-mono"
                            style={{
                                fontSize: 10, color: "var(--t-ink3)",
                                padding: "32px 16px", letterSpacing: "0.16em",
                                fontStyle: "italic", textAlign: "center",
                            }}
                        >
                            · type · enter · pick verified
                        </p>
                    ) : pickerResults.length === 0 ? (
                        <div
                            data-testid="curate-picker-empty"
                            style={{ padding: "26px 18px", textAlign: "center" }}
                        >
                            <p
                                className="t-serif italic"
                                style={{
                                    fontSize: 14, color: "var(--t-ink2)",
                                    lineHeight: 1.55, marginBottom: 6,
                                }}
                            >
                                Nothing verified matched
                                {" "}
                                <span style={{ color: "var(--t-ink)", fontStyle: "normal", fontWeight: 600 }}>
                                    "{pickerLastQ}"
                                </span>
                                .
                            </p>
                            <p
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 9, color: "var(--t-ink3)",
                                    letterSpacing: "0.16em", fontStyle: "italic",
                                }}
                            >
                                no guess · try a different phrasing or a city
                            </p>
                        </div>
                    ) : pickerResults.map((v) => (
                        // Founder ask 11 Feb 2026 PM · split click target ·
                        // the row body opens the existing RestaurantDrawer
                        // (same detail the member sees) so the curator can
                        // read about the venue before adding it. The
                        // small `+ / ✓` button stays as the explicit
                        // toggle. We render the whole row as a <div>
                        // (not a <button>) so the inner toggle button
                        // doesn't nest inside another button (HTML-illegal
                        // + caused click swallowing).
                        <div
                            key={v.id}
                            data-testid={`curate-picker-result-${v.id}`}
                            role={typeof onOpenDetail === "function" ? "button" : undefined}
                            aria-label={typeof onOpenDetail === "function"
                                ? `View details for ${v.name}` : undefined}
                            tabIndex={typeof onOpenDetail === "function" ? 0 : undefined}
                            onClick={() => {
                                if (typeof onOpenDetail === "function") onOpenDetail(v);
                                else togglePick(v);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    if (typeof onOpenDetail === "function") onOpenDetail(v);
                                    else togglePick(v);
                                }
                            }}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                width: "100%",
                                textAlign: "left",
                                padding: "12px 14px",
                                background: pickedIds.has(v.id)
                                    ? "var(--t-brass-wash)"
                                    : "transparent",
                                border: pickedIds.has(v.id)
                                    ? "1px solid var(--t-brass2)"
                                    : "1px solid transparent",
                                borderRadius: 10,
                                cursor: "pointer",
                                marginBottom: 4,
                                transition: "background 0.15s, border 0.15s",
                            }}
                            onMouseEnter={(e) => {
                                if (!pickedIds.has(v.id)) {
                                    e.currentTarget.style.background = "var(--t-card)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!pickedIds.has(v.id)) {
                                    e.currentTarget.style.background = "transparent";
                                }
                            }}
                        >
                            <button
                                type="button"
                                data-testid={`curate-picker-toggle-${v.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePick(v);
                                }}
                                aria-label={pickedIds.has(v.id)
                                    ? `Remove ${v.name}` : `Add ${v.name}`}
                                aria-pressed={pickedIds.has(v.id)}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24, height: 24,
                                    borderRadius: 999,
                                    border: pickedIds.has(v.id)
                                        ? "1px solid var(--t-brass)"
                                        : "1px solid var(--t-line2)",
                                    background: pickedIds.has(v.id)
                                        ? "var(--t-brass)" : "transparent",
                                    color: pickedIds.has(v.id)
                                        ? "var(--t-paper2)" : "var(--t-ink2)",
                                    fontSize: 14,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    padding: 0,
                                }}
                            >
                                {pickedIds.has(v.id) ? "\u2713" : "+"}
                            </button>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--t-ink)" }}>
                                    {v.name}
                                </div>
                                <div
                                    className="t-mono"
                                    style={{
                                        fontSize: 10, color: "var(--t-ink3)",
                                        letterSpacing: "0.05em", marginTop: 3,
                                    }}
                                >
                                    {v.city || ""}
                                    {v.rating ? ` · ★ ${Number(v.rating).toFixed(1)}` : ""}
                                    {v.route ? ` · ${v.route}` : ""}
                                    {v.route_via ? ` via ${v.route_via}` : ""}
                                </div>
                            </div>
                            {typeof onOpenDetail === "function" ? (
                                <span
                                    aria-hidden="true"
                                    className="t-mono uppercase"
                                    style={{
                                        fontSize: 9,
                                        color: "var(--t-ink3)",
                                        letterSpacing: "0.18em",
                                        opacity: 0.7,
                                        flexShrink: 0,
                                    }}
                                >
                                    details ↗
                                </span>
                            ) : null}
                        </div>
                    ))}
                </div>

                <div className="flex flex-wrap gap-2" data-testid="curate-selected">
                    {picks.map((p) => (
                        <span
                            key={p.id}
                            data-testid={`curate-selected-${p.id}`}
                            style={{
                                fontSize: 12,
                                background: "var(--t-card)",
                                border: "1px solid var(--t-brass2)",
                                color: "var(--t-ink)",
                                borderRadius: 16, padding: "4px 11px",
                                display: "inline-flex", gap: 6, alignItems: "center",
                            }}
                        >
                            {p.name}
                            <button
                                type="button"
                                onClick={() => removePick(p.id)}
                                aria-label={`Remove ${p.name}`}
                                style={{
                                    background: "transparent", border: 0,
                                    color: "var(--t-ink3)", cursor: "pointer",
                                    fontSize: 14, lineHeight: 1, padding: 0,
                                }}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            </section>

            {/* RIGHT · Brief + compose */}
            <section data-testid="curate-brief-section">
                <h2
                    className="t-serif"
                    style={{
                        fontSize: 26, fontWeight: 500,
                        lineHeight: 1.15, marginBottom: 4,
                        letterSpacing: "-0.01em",
                    }}
                >
                    2 · Brief
                </h2>
                <p style={{ fontSize: 13, color: "var(--t-ink3)", marginBottom: 18 }}>
                    Plain-language. The model writes the telling around these verified venues.
                </p>
                <textarea
                    data-testid="curate-brief"
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder="e.g. a vegetarian's long weekend in Lisbon — one special dinner, two easy lunches, somewhere for pastéis"
                    rows={6}
                    style={{
                        width: "100%", padding: "14px 16px",
                        background: "var(--t-paper2)",
                        color: "var(--t-ink)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 14, fontSize: 15, lineHeight: 1.55,
                        fontFamily: "'Eb Garamond', serif", outline: "none",
                        resize: "vertical",
                    }}
                />
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <input
                        data-testid="curate-occasion"
                        value={occasion}
                        onChange={(e) => setOccasion(e.target.value)}
                        placeholder="Occasion (optional)"
                        style={pickerInputStyle}
                    />
                    <input
                        data-testid="curate-locale"
                        value={locale}
                        onChange={(e) => setLocale(e.target.value)}
                        placeholder="City / region (optional)"
                        style={pickerInputStyle}
                    />
                </div>
                <button
                    type="button"
                    data-testid="curate-compose"
                    onClick={compose}
                    disabled={composing || brief.trim().length < 4 || picks.length === 0}
                    style={{
                        marginTop: 20, width: "100%", height: 54,
                        background: (composing || brief.trim().length < 4 || picks.length === 0)
                            ? "var(--t-line)"
                            : "var(--t-ink)",
                        color: "var(--t-paper2)",
                        border: 0, borderRadius: 14,
                        fontSize: 16, fontWeight: 600,
                        cursor: (composing || brief.trim().length < 4 || picks.length === 0)
                            ? "not-allowed" : "pointer",
                        letterSpacing: "0.01em",
                    }}
                >
                    {composing ? "Composing…" : `✦ Compose dossier · ${picks.length} venue${picks.length === 1 ? "" : "s"}`}
                </button>
                <p
                    className="t-mono"
                    style={{
                        fontSize: 10, color: "var(--t-ink3)",
                        textAlign: "center", marginTop: 12,
                        fontStyle: "italic", letterSpacing: "0.02em",
                    }}
                    data-testid="curate-firewall-line"
                >
                    composing on verified slate · reading not recalling · founder approves
                </p>
            </section>
        </div>
    );
}

const pickerInputStyle = {
    width: "100%", padding: "11px 14px",
    background: "var(--t-paper2)",
    color: "var(--t-ink)",
    border: "1px solid var(--t-line2)",
    borderRadius: 10, fontSize: 14,
    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
    outline: "none",
};


// ── PickTextureChips · BUG 2 fix · 6 Jun 2026 evening ──────────────
// Renders anchor texture (chef · signature · must-try · insider-move)
// + atmosphere flags (· Google Places) for a single pick. The slate
// showed these · the draft MUST show them too · texturally identical
// is the doctrine. The chips are read from `pick.slot_chips` which
// the backend builds via `_build_slot_chips` from the resolved venue
// (with anchor texture re-overlaid at compose time).
//
// Note: routine slot chips (rating · route · address) are rendered
// separately by the slot row above this component · this component
// shows ONLY the editorial + atmosphere chips.
function PickTextureChips({ chips }) {
    const EDITORIAL_LABELS = new Set([
        "chef", "distinction", "signature menu", "must try",
        "dietary note", "insider move",
    ]);
    const ATMOSPHERE_LABELS = new Set([
        "vegetarian food", "reservable", "dine-in",
        "outdoor seating", "payment",
    ]);
    const editorial = chips.filter((c) => EDITORIAL_LABELS.has(c.label));
    const atmosphere = chips.filter((c) => ATMOSPHERE_LABELS.has(c.label));
    if (editorial.length === 0 && atmosphere.length === 0) return null;
    return (
        <div className="mt-4" data-testid="curate-pick-texture">
            {editorial.length > 0 ? (
                <dl
                    className="grid"
                    style={{
                        gridTemplateColumns: "max-content 1fr",
                        gap: "6px 14px",
                        fontSize: 13, lineHeight: 1.5,
                        marginTop: 4,
                    }}
                >
                    {editorial.map((c) => (
                        <React.Fragment key={c.label}>
                            <dt
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 9,
                                    letterSpacing: "0.16em",
                                    color: "var(--t-brass)",
                                    fontWeight: 700,
                                    paddingTop: 4,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {c.label}
                            </dt>
                            <dd
                                className="t-serif"
                                style={{
                                    color: "var(--t-ink)",
                                    margin: 0,
                                }}
                            >
                                <span>{c.value}</span>
                                {c.source ? (
                                    <span
                                        className="t-mono"
                                        style={{
                                            fontSize: 9,
                                            letterSpacing: "0.12em",
                                            color: "var(--t-ink3)",
                                            marginLeft: 6,
                                        }}
                                    >
                                        · {c.source}
                                    </span>
                                ) : null}
                            </dd>
                        </React.Fragment>
                    ))}
                </dl>
            ) : null}

            {atmosphere.length > 0 ? (
                <div
                    className="flex flex-wrap gap-2 mt-3"
                    data-testid="curate-pick-atmosphere"
                >
                    {atmosphere.map((c) => (
                        <span
                            key={c.label}
                            className="t-mono uppercase"
                            style={{
                                fontSize: 9,
                                letterSpacing: "0.14em",
                                color: "var(--t-ink2)",
                                background: "var(--t-paper2)",
                                border: "1px solid var(--t-line2)",
                                borderRadius: 999,
                                padding: "3px 9px",
                            }}
                        >
                            {c.label}
                            {c.value && c.value !== "yes" && c.value !== "served"
                                ? ` · ${c.value}`
                                : ""}
                            <span style={{ color: "var(--t-ink3)" }}>
                                {" · Google"}
                            </span>
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
}


function DraftView({ draft, onApprove, approving, onNew, onUpdate }) {
    const unresolved = draft.unresolved_venue_ids || draft.dropped_venue_ids || [];
    const needsVoiceIds = draft.needs_voice_venue_ids || [];
    const selectedCount = draft.selected_count;
    const deliveredCount = draft.delivered_count ?? (draft.picks || []).length;
    const countMismatch =
        typeof selectedCount === "number" &&
        selectedCount !== deliveredCount + unresolved.length;
    return (
        <article data-testid="curate-draft">
            <div className="flex items-center justify-between mb-4">
                <div
                    className="t-mono uppercase"
                    style={{ fontSize: 10, letterSpacing: "0.22em", color: "var(--t-ink3)" }}
                >
                    draft · {draft.draft_id}
                </div>
                <button
                    type="button"
                    data-testid="curate-new-draft"
                    onClick={onNew}
                    style={{
                        fontSize: 11, color: "var(--t-ink2)",
                        background: "transparent",
                        border: "1px solid var(--t-line2)", borderRadius: 18,
                        padding: "5px 12px", cursor: "pointer",
                    }}
                >
                    + new draft
                </button>
            </div>

            {/* Doctrine surfacing · BUG 1 fix 6 Jun 2026 · "A draft
                must NEVER contain fewer venues than I selected
                without telling me." Failure banner appears the moment
                count drifts · NEVER silent. */}
            {unresolved.length > 0 || countMismatch ? (
                <div
                    data-testid="curate-unresolved-banner"
                    style={{
                        background: "rgba(180, 60, 60, 0.06)",
                        border: "1px solid rgba(180, 60, 60, 0.45)",
                        borderRadius: 12,
                        padding: "12px 16px",
                        marginBottom: 18,
                    }}
                >
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            color: "#A33232", fontWeight: 700, marginBottom: 6,
                        }}
                    >
                        · attention · selection vs delivered mismatch
                    </div>
                    <div
                        className="t-serif"
                        style={{ fontSize: 14, color: "var(--t-ink2)" }}
                    >
                        You selected <b>{selectedCount}</b> venues.
                        The draft carries <b>{deliveredCount}</b>.
                        {unresolved.length > 0 ? (
                            <>
                                {" "}<b>{unresolved.length}</b> couldn't be re-verified
                                live and were dropped honestly (not silently):
                                <code style={{
                                    display: "block", marginTop: 6,
                                    fontSize: 11, color: "var(--t-ink3)",
                                    fontFamily: "var(--t-mono-stack)",
                                }}>
                                    {unresolved.join(", ")}
                                </code>
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {/* Per-pick voice-missing surfacing · the founder ear-test
                doctrine: every approved venue MUST render · if Claude
                omitted a line, we tell her so she can fill it. */}
            {needsVoiceIds.length > 0 ? (
                <div
                    data-testid="curate-needs-voice-banner"
                    style={{
                        background: "rgba(184, 153, 104, 0.10)",
                        border: "1px solid rgba(184, 153, 104, 0.55)",
                        borderRadius: 12,
                        padding: "10px 14px",
                        marginBottom: 18,
                    }}
                >
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            color: "var(--t-brass)", fontWeight: 700,
                        }}
                    >
                        · {needsVoiceIds.length} venue{needsVoiceIds.length === 1 ? "" : "s"} need{needsVoiceIds.length === 1 ? "s" : ""} a voice line
                    </div>
                    <div
                        className="t-serif italic"
                        style={{ fontSize: 13, color: "var(--t-ink3)", marginTop: 4 }}
                    >
                        The verified facts are present · the Concierge® line is pending.
                    </div>
                </div>
            ) : null}
            {/* Doctrine confidence-header · 7 Jun 2026 evening
                · founder-mandated working-sheet flow. The header
                surfaces (a) verified vs pending split, (b) WHICH
                gates each pending venue awaits, with EACH gate-
                chip clickable to open the desk queue filtered to
                exactly those venues. The dossier becomes a live
                working-sheet for the desk, not a static PDF.

                The chip is rendered as a real <a> to /table/desk
                with `?draft_id=...&gates=<gate_id>` · the desk view
                reads those params, hits the queue admin endpoint,
                and renders the filtered queue with dispose actions.
            */}
            {(typeof draft.verified_count === "number" || typeof draft.pending_count === "number") ? (
                <div
                    data-testid="curate-confidence-header"
                    style={{
                        marginBottom: 18,
                        padding: "10px 16px",
                        background: "var(--t-card)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 12,
                    }}
                >
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            color: "var(--t-ink3)",
                            marginBottom: (draft.gates_pending_breakdown || []).length ? 6 : 0,
                        }}
                    >
                        {draft.pending_count === 0 ? (
                            <span style={{ color: "var(--t-verified)" }}>
                                All {draft.verified_count} venues phone-verified · valid as of today
                            </span>
                        ) : (
                            <span>
                                <span data-testid="confidence-verified-count">
                                    {draft.verified_count || 0}
                                </span>
                                {" phone-verified · "}
                                <span data-testid="confidence-clean-count">
                                    {draft.clean_traceable_count ?? (draft.pending_count - (draft.conflicts_count || 0)) ?? 0}
                                </span>
                                {" clean-traceable"}
                                {(draft.conflicts_count || 0) > 0 ? (
                                    <>
                                        {" · "}
                                        <span
                                            data-testid="confidence-conflicts-count"
                                            style={{ color: "var(--t-brass)", fontWeight: 700 }}
                                        >
                                            {draft.conflicts_count} with conflicts needing correction
                                        </span>
                                    </>
                                ) : null}
                            </span>
                        )}
                    </div>
                    {(draft.gates_pending_breakdown || []).length ? (
                        <div
                            data-testid="confidence-gates-breakdown"
                            className="t-mono"
                            style={{
                                fontSize: 11, color: "var(--t-ink3)",
                                display: "flex", flexWrap: "wrap", gap: 8,
                                alignItems: "center",
                            }}
                        >
                            <span style={{ letterSpacing: "0.02em" }}>awaiting</span>
                            {draft.gates_pending_breakdown.map((g) => (
                                <a
                                    key={g.gate_id}
                                    data-testid={`confidence-gate-chip-${g.gate_id}`}
                                    href={`/table/desk?draft_id=${encodeURIComponent(draft.draft_id)}&gates=${encodeURIComponent(g.gate_id)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        padding: "3px 9px",
                                        background: "var(--t-cream, #f8f5ee)",
                                        border: "1px solid var(--t-line2)",
                                        borderRadius: 30,
                                        color: "var(--t-ink2)",
                                        textDecoration: "none",
                                        fontSize: 10.5,
                                        letterSpacing: "0.02em",
                                        cursor: "pointer",
                                        transition: "border-color 0.15s ease",
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--t-brass)"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--t-line2)"; }}
                                    title="Open the desk queue filtered to these venues"
                                >
                                    {g.label} × {g.count}
                                </a>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
            <h1
                className="t-serif"
                style={{
                    fontSize: 38, fontWeight: 500, lineHeight: 1.1,
                    letterSpacing: "-0.015em", color: "var(--t-ink)",
                }}
                data-testid="curate-title"
            >
                {draft.title}
            </h1>
            {draft.intro ? (
                <p
                    data-testid="curate-intro"
                    className="t-serif"
                    style={{
                        fontSize: 17, color: "var(--t-ink2)",
                        lineHeight: 1.65, marginTop: 18,
                        fontStyle: "italic",
                    }}
                >
                    {draft.intro}
                </p>
            ) : null}

            <div className="mt-8 space-y-6" data-testid="curate-picks">
                {(draft.picks || []).map((p, i) => (
                    <article
                        key={p.venue_id}
                        data-testid={`curate-pick-${i}`}
                        style={{
                            background: "var(--t-card)",
                            border: "1px solid var(--t-line2)",
                            borderRadius: 14,
                            padding: "18px 22px",
                        }}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <h3
                                className="t-serif"
                                style={{
                                    fontSize: 22, fontWeight: 500,
                                    color: "var(--t-ink)",
                                    lineHeight: 1.2,
                                }}
                            >
                                {p.name}
                            </h3>
                            {p.needs_voice ? (
                                <span
                                    data-testid={`curate-pick-needs-voice-${i}`}
                                    className="t-mono uppercase"
                                    style={{
                                        fontSize: 9, letterSpacing: "0.16em",
                                        color: "var(--t-brass)",
                                        background: "var(--t-brass-wash)",
                                        padding: "3px 9px",
                                        borderRadius: 999,
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                    }}
                                >
                                    voice pending
                                </span>
                            ) : null}
                        </div>
                        <p
                            style={{
                                fontSize: 15,
                                color: p.needs_voice
                                    ? "var(--t-brass)"
                                    : "var(--t-ink2)",
                                fontStyle: p.needs_voice ? "italic" : undefined,
                                lineHeight: 1.65, marginTop: 8,
                            }}
                            data-testid={`curate-pick-voice-${i}`}
                        >
                            {p.voice}
                        </p>
                        {/* Verified slot row · facts attached BY US */}
                        <div
                            className="t-mono uppercase mt-4 flex flex-wrap gap-x-4 gap-y-1"
                            style={{
                                fontSize: 10, letterSpacing: "0.14em",
                                color: "var(--t-ink3)",
                            }}
                            data-testid={`curate-pick-slots-${i}`}
                        >
                            {p.verified_slots?.rating ? (
                                <span>★ {Number(p.verified_slots.rating).toFixed(1)}</span>
                            ) : null}
                            {p.verified_slots?.user_ratings_total ? (
                                <span>· {p.verified_slots.user_ratings_total.toLocaleString()} reviews</span>
                            ) : null}
                            {p.verified_slots?.route ? (
                                <span>· {p.verified_slots.route}</span>
                            ) : null}
                            {p.verified_slots?.route_via ? (
                                <span>via {p.verified_slots.route_via}</span>
                            ) : null}
                            {p.verified_slots?.map_url ? (
                                <a
                                    href={p.verified_slots.map_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "var(--t-brass)", textDecoration: "none" }}
                                >
                                    map ↗
                                </a>
                            ) : null}
                        </div>

                        {/* BUG 2 fix · 6 Jun 2026 evening · anchor
                            texture (chef / signature / must-try /
                            insider-move) + atmosphere flags MUST
                            render in the draft the same way they
                            render on the slate. Without this, the
                            founder eyeballed Daigo on the slate
                            WITH texture and saw it in the draft
                            WITHOUT — texturally identical was the
                            doctrine. */}
                        <PickTextureChips chips={p.slot_chips || []} />

                        {/* P4 · Ask a Concierge® pill (founder ear-test
                            6 Jun 2026 evening · Q2-ii). Client-facing.
                            Default routes to founder WhatsApp · Mira
                            target wired but dark until founder
                            confirms her behind the Concierge® mark. */}
                        <AskConciergePill
                            venue={{ name: p.name, city: p.verified_slots?.city }}
                            draftId={draft.draft_id}
                            testIdPrefix={`ask-concierge-${i}`}
                        />
                    </article>
                ))}
            </div>

            {/* E11 · "+ add a verified venue" refine flow ·
                founder ear-test 6 Jun 2026 ·
                lives between the picks grid and the outro so the
                founder's eye sees it after reviewing what's already
                on the dossier. */}
            <AppendVenuePanel
                draft={draft}
                onUpdate={onUpdate}
            />

            {draft.outro ? (
                <p
                    data-testid="curate-outro"
                    className="t-serif"
                    style={{
                        fontSize: 16, color: "var(--t-ink2)",
                        lineHeight: 1.65, marginTop: 22,
                        fontStyle: "italic",
                    }}
                >
                    {draft.outro}
                </p>
            ) : null}

            <div className="t-hairline my-8" />

            <div className="flex items-center gap-4 flex-wrap">
                <button
                    type="button"
                    data-testid="curate-approve"
                    onClick={onApprove}
                    disabled={draft.approved || approving}
                    style={{
                        height: 50, padding: "0 26px", borderRadius: 14,
                        background: draft.approved
                            ? "var(--t-verified-wash)"
                            : "var(--t-ink)",
                        color: draft.approved
                            ? "var(--t-verified)"
                            : "var(--t-paper2)",
                        border: draft.approved
                            ? "1px solid var(--t-verified)"
                            : 0,
                        fontSize: 14, fontWeight: 600,
                        cursor: draft.approved || approving ? "default" : "pointer",
                        letterSpacing: "0.01em",
                    }}
                >
                    {draft.approved
                        ? "✓ Approved · ready to send"
                        : approving ? "Approving…" : "Approve draft"}
                </button>
                <p
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.16em",
                        color: "var(--t-ink3)", fontStyle: "italic",
                    }}
                >
                    {draft.approved
                        ? "founder approval recorded · ready for the three outputs"
                        : "no external send until you approve"}
                </p>
            </div>

            <ThreeOutputsPanel draft={draft} />

            {Array.isArray(draft.dropped_venue_ids) && draft.dropped_venue_ids.length ? (
                <p
                    className="t-mono"
                    data-testid="curate-dropped-notice"
                    style={{
                        fontSize: 11, color: "var(--t-ink3)", marginTop: 14,
                        fontStyle: "italic",
                    }}
                >
                    note · {draft.dropped_venue_ids.length} venue id(s) didn't resolve
                    verified · dropped from this dossier
                </p>
            ) : null}
        </article>
    );
}



// ── E11 · AppendVenuePanel ───────────────────────────────────────
// "+ add a verified venue" — the founder's "forgot Indian" use case.
// Below the picks grid, above the outro. Type a follow-up query,
// see verified candidates (already-picked venues filtered out),
// pick one, the model writes ONE voice paragraph anchored to the
// original brief, the draft refreshes in place.
//
// Doctrine invariants (mirror the backend):
//   - Every candidate carries a place_id from the live pipeline.
//   - The model NEVER invents a venue.
//   - The voice paragraph is composed from verified slots ONLY.
//   - A venue already in the draft can never be re-added.
//
function AppendVenuePanel({ draft, onUpdate }) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [candidates, setCandidates] = React.useState(null);
    const [searching, setSearching] = React.useState(false);
    const [appendingId, setAppendingId] = React.useState(null);
    const [error, setError] = React.useState(null);

    const draftId = draft?.draft_id;
    if (!draftId) return null;

    const reset = () => {
        setQuery("");
        setCandidates(null);
        setError(null);
        setAppendingId(null);
    };

    const closePanel = () => {
        setOpen(false);
        reset();
    };

    const runDiscover = async () => {
        const q = query.trim();
        if (q.length < 3) {
            setError("Type at least 3 characters · e.g. 'good Indian place'");
            return;
        }
        setSearching(true);
        setError(null);
        setCandidates(null);
        try {
            const data = await discoverMoreForDraft(draftId, q);
            setCandidates(data.venues || []);
            if ((data.venues || []).length === 0) {
                setError(
                    "The pipeline returned no verified candidates for that query · " +
                    "try widening the wording, or your Concierge\u00ae will curate this on the ground."
                );
            }
        } catch (e) {
            setError(e?.body?.detail?.message || e?.message || "Discovery failed");
        } finally {
            setSearching(false);
        }
    };

    const appendOne = async (venueId) => {
        setAppendingId(venueId);
        setError(null);
        try {
            const updated = await appendPickToDraft(draftId, venueId);
            onUpdate(updated);
            // Soft success · close the panel; the new pick appears
            // at the bottom of the picks grid above.
            closePanel();
        } catch (e) {
            setError(e?.body?.detail?.message || e?.body?.detail || e?.message || "Append failed");
        } finally {
            setAppendingId(null);
        }
    };

    if (!open) {
        return (
            <div
                data-testid="curate-append-venue-cta"
                style={{
                    margin: "32px 0 28px",
                    padding: "22px 24px",
                    background: "var(--t-paper2)",
                    border: "1px dashed var(--t-line2)",
                    borderRadius: 14,
                    textAlign: "center",
                }}
            >
                <div
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.22em",
                        color: "var(--t-brass)", marginBottom: 8,
                    }}
                >
                    · refine
                </div>
                <p
                    className="t-serif italic"
                    style={{
                        fontSize: 14, color: "var(--t-ink3)",
                        margin: "0 0 14px", lineHeight: 1.5,
                    }}
                >
                    Forgot a register? Add one more verified venue without rebuilding the brief.
                </p>
                <button
                    type="button"
                    data-testid="curate-append-venue-open"
                    onClick={() => setOpen(true)}
                    style={{
                        padding: "10px 20px",
                        borderRadius: 999,
                        background: "var(--t-ink)",
                        color: "var(--t-paper2)",
                        border: 0,
                        fontSize: 12, fontWeight: 700,
                        letterSpacing: "0.04em",
                        cursor: "pointer",
                    }}
                >
                    + add a verified venue
                </button>
            </div>
        );
    }

    return (
        <div
            data-testid="curate-append-venue-panel"
            style={{
                margin: "32px 0 28px",
                padding: "24px 24px 26px",
                background: "var(--t-paper2)",
                border: "1px solid var(--t-brass)",
                borderRadius: 14,
            }}
        >
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                <div>
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.22em",
                            color: "var(--t-brass)",
                        }}
                    >
                        · adding to the dossier
                    </div>
                    <h3
                        className="t-serif"
                        style={{
                            fontSize: 18, fontWeight: 500,
                            color: "var(--t-ink)", margin: "4px 0 0",
                            lineHeight: 1.25,
                        }}
                    >
                        What kind of place are we adding?
                    </h3>
                </div>
                <button
                    type="button"
                    data-testid="curate-append-venue-close"
                    onClick={closePanel}
                    style={{
                        background: "transparent",
                        border: "1px solid var(--t-line2)",
                        color: "var(--t-ink3)",
                        borderRadius: 999,
                        padding: "4px 12px",
                        fontSize: 11,
                        cursor: "pointer",
                    }}
                >
                    cancel
                </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <input
                    data-testid="curate-append-venue-input"
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") runDiscover(); }}
                    placeholder="e.g. good Indian restaurant"
                    style={{
                        flex: 1, minWidth: 240,
                        padding: "10px 14px",
                        background: "var(--t-paper)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 10,
                        fontSize: 14, color: "var(--t-ink)",
                        outline: "none",
                    }}
                />
                <button
                    type="button"
                    data-testid="curate-append-venue-search"
                    onClick={runDiscover}
                    disabled={searching || query.trim().length < 3}
                    style={{
                        padding: "10px 18px",
                        borderRadius: 999,
                        background: searching || query.trim().length < 3
                            ? "var(--t-line2)" : "var(--t-ink)",
                        color: "var(--t-paper2)",
                        border: 0,
                        fontSize: 12, fontWeight: 700,
                        letterSpacing: "0.02em",
                        cursor: searching ? "wait" : "pointer",
                    }}
                >
                    {searching ? "· searching…" : "Find verified options"}
                </button>
            </div>

            <p
                className="t-serif italic"
                style={{
                    fontSize: 12, color: "var(--t-ink3)",
                    margin: "10px 0 0", lineHeight: 1.5,
                }}
            >
                Anchored to <strong style={{ color: "var(--t-ink2)" }}>
                    {draft?.destination?.city || "this dossier's city"}
                </strong> · verified by the same pipeline · venues already on
                the dossier are filtered out automatically.
            </p>

            {error ? (
                <div
                    data-testid="curate-append-venue-error"
                    className="t-serif italic"
                    style={{
                        marginTop: 14,
                        padding: "10px 14px",
                        background: "var(--t-paper)",
                        border: "1px dashed var(--t-brass)",
                        borderRadius: 10,
                        fontSize: 13, color: "var(--t-ink2)",
                        lineHeight: 1.5,
                    }}
                >
                    {error}
                </div>
            ) : null}

            {Array.isArray(candidates) && candidates.length > 0 ? (
                <div style={{ marginTop: 18 }}>
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            color: "var(--t-ink3)", marginBottom: 8,
                        }}
                    >
                        verified candidates · {candidates.length}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {candidates.map((v, i) => (
                            <AppendCandidateCard
                                key={v.id || v.place_id || i}
                                venue={v}
                                index={i}
                                onAppend={() => appendOne(v.id)}
                                composing={appendingId === v.id}
                                disabled={!!appendingId && appendingId !== v.id}
                            />
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}


function AppendCandidateCard({ venue, index, onAppend, composing, disabled }) {
    return (
        <div
            data-testid={`curate-append-candidate-${index}`}
            style={{
                background: "var(--t-paper)",
                border: "1px solid var(--t-line2)",
                borderRadius: 12,
                padding: "12px 14px",
            }}
        >
            <div className="flex items-start gap-2 mb-2">
                <span
                    className="t-mono"
                    style={{
                        fontSize: 10, color: "var(--t-brass)",
                        fontWeight: 700, marginTop: 2,
                    }}
                >
                    {String(index + 1).padStart(2, "0")}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        className="t-serif"
                        style={{
                            fontSize: 14, color: "var(--t-ink)",
                            fontWeight: 600, lineHeight: 1.25,
                        }}
                    >
                        {venue.name}
                    </div>
                    {venue.city ? (
                        <div
                            className="t-mono"
                            style={{
                                fontSize: 10, color: "var(--t-ink3)",
                                marginTop: 2,
                            }}
                        >
                            {venue.city}
                        </div>
                    ) : null}
                </div>
                {venue.rating ? (
                    <span
                        className="t-mono"
                        style={{
                            fontSize: 10, color: "var(--t-ink3)",
                            marginTop: 2,
                        }}
                    >
                        ★ {Number(venue.rating).toFixed(1)}
                    </span>
                ) : null}
            </div>
            <button
                type="button"
                data-testid={`curate-append-candidate-add-${index}`}
                onClick={onAppend}
                disabled={composing || disabled}
                style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: composing
                        ? "var(--t-line2)"
                        : disabled ? "var(--t-line2)" : "var(--t-ink)",
                    color: composing || disabled
                        ? "var(--t-ink3)" : "var(--t-paper2)",
                    border: 0,
                    fontSize: 11, fontWeight: 700,
                    letterSpacing: "0.04em",
                    cursor: composing
                        ? "wait" : disabled ? "not-allowed" : "pointer",
                }}
            >
                {composing ? "· composing voice…" : "+ append to dossier"}
            </button>
        </div>
    );
}



function ErrorPanel({ error, onRetry }) {
    return (
        <div className="text-center py-12" data-testid="curate-error">
            <p style={{ fontSize: 16, color: "var(--t-ink)", marginBottom: 16 }}>
                {typeof error === "string" ? error : "Curation failed."}
            </p>
            <button
                type="button"
                data-testid="curate-retry"
                onClick={onRetry}
                style={{
                    height: 46, padding: "0 22px", borderRadius: 12,
                    background: "var(--t-ink)", color: "var(--t-paper2)",
                    border: 0, fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
            >
                Try again
            </button>
        </div>
    );
}



// ── Three outputs panel · post-approval ──────────────────────────────
//
// Founder direction (5 Jun 2026 hand-off):
//   "From ONE approved draft, three shapes: Concierge® dossier ·
//    WhatsApp message · in-app collection. NO model, NO composition.
//    Nothing sends unless `approved` is True · `_require_approved()`
//    409s otherwise."
//
// The three cards stay greyed-out before approval (UX cue + the
// backend gate is the real enforcement). After approval, each card
// is a small action: download PDF · copy/preview WhatsApp · view
// collection JSON · optional send-WhatsApp form.

function ThreeOutputsPanel({ draft }) {
    const ready = !!draft?.approved;

    return (
        <section
            data-testid="curate-three-outputs"
            style={{
                marginTop: 28,
                padding: "20px 22px",
                background: ready ? "var(--t-paper2)" : "var(--t-paper)",
                border: `1px dashed ${ready ? "var(--t-brass2)" : "var(--t-line2)"}`,
                borderRadius: 16,
                opacity: ready ? 1 : 0.55,
            }}
        >
            <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
                <div>
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.22em",
                            color: "var(--t-brass)", fontWeight: 700,
                        }}
                    >
                        the three outputs
                    </div>
                    <h3
                        className="t-serif"
                        style={{
                            fontSize: 20, fontWeight: 500, marginTop: 4,
                            color: "var(--t-ink)", lineHeight: 1.2,
                        }}
                    >
                        Reshape the approved draft · no composition
                    </h3>
                </div>
                <span
                    className="t-mono uppercase"
                    style={{
                        fontSize: 9, letterSpacing: "0.18em",
                        color: ready ? "var(--t-verified)" : "var(--t-ink3)",
                        fontStyle: "italic",
                    }}
                    data-testid="three-outputs-gate-status"
                >
                    {ready
                        ? "✓ approved · outputs unlocked"
                        : "· locked until approval"}
                </span>
            </div>
            <div
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
                <DossierCard draft={draft} ready={ready} />
                <DossierDocxCard draft={draft} ready={ready} />
                <WhatsAppCard draft={draft} ready={ready} />
                <CollectionCard draft={draft} ready={ready} />
            </div>
        </section>
    );
}


function DossierCard({ draft, ready }) {
    return (
        <OutputCard
            testId="output-dossier"
            label="Concierge® dossier · PDF"
            sub="locked send copy · house format · navy/cream/brass"
        >
            <a
                data-testid="output-dossier-download"
                href={ready ? dossierUrl(draft.draft_id) : undefined}
                onClick={(e) => { if (!ready) e.preventDefault(); }}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "9px 16px", borderRadius: 999,
                    background: ready ? "var(--t-ink)" : "var(--t-line2)",
                    color: ready ? "var(--t-paper2)" : "var(--t-ink3)",
                    textDecoration: "none",
                    fontSize: 12, fontWeight: 600,
                    cursor: ready ? "pointer" : "not-allowed",
                }}
            >
                Download PDF
            </a>
        </OutputCard>
    );
}


// Magic Curation Phase B3 · the editable Word output.
// House format · founder can open, tweak, re-export.
function DossierDocxCard({ draft, ready }) {
    return (
        <OutputCard
            testId="output-dossier-docx"
            label="Concierge® dossier · Word"
            sub="editable working copy · same house format"
        >
            <a
                data-testid="output-dossier-docx-download"
                href={ready ? dossierDocxUrl(draft.draft_id) : undefined}
                onClick={(e) => { if (!ready) e.preventDefault(); }}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "9px 16px", borderRadius: 999,
                    background: ready ? "var(--t-ink)" : "var(--t-line2)",
                    color: ready ? "var(--t-paper2)" : "var(--t-ink3)",
                    textDecoration: "none",
                    fontSize: 12, fontWeight: 600,
                    cursor: ready ? "pointer" : "not-allowed",
                }}
            >
                Download Word
            </a>
        </OutputCard>
    );
}


function WhatsAppCard({ draft, ready }) {
    const [text, setText] = useState("");
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [sendState, setSendState] = useState(null); // null | 'sent' | {error}
    const [copied, setCopied] = useState(false);    const previewText = useCallback(async () => {
        if (!ready || !draft?.draft_id) return;
        setLoading(true);
        try {
            const res = await fetchCurationWhatsApp(draft.draft_id);
            setText(res.text || "");
        } catch (e) {
            setText("· preview failed · " + (e?.message || "unknown"));
        } finally {
            setLoading(false);
        }
    }, [draft?.draft_id, ready]);

    useEffect(() => { if (ready) previewText(); }, [ready, previewText]);

    const copy = async () => {
        // Founder ear-test 5 Jun 2026 iter22 nit: the '✓ copied'
        // affordance must be obvious. Hold the visual state for
        // ~1.6s · use a single source of truth (`copied`) so a
        // rapid double-click doesn't fight a stale setTimeout.
        if (!text) return;
        const ok = await safeClipboardWrite(text);
        if (!ok) return;
        setCopied(true);
        if (copy._t) clearTimeout(copy._t);
        copy._t = setTimeout(() => setCopied(false), 1600);
    };

    const send = async () => {
        if (!ready || !phone) return;
        setSendState(null);
        setLoading(true);
        try {
            const publicUrl = (typeof window !== "undefined"
                && window.location?.origin) || "";
            const res = await sendCurationWhatsApp(draft.draft_id, {
                to_phone: phone, public_url: publicUrl,
            });
            if (res.sent) setSendState("sent");
            else setSendState({ error: res.reason || "Gupshup said no" });
        } catch (e) {
            setSendState({ error: e?.message || "send failed" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <OutputCard
            testId="output-whatsapp"
            label="WhatsApp message"
            sub="tight · sendable · dossier as download link"
        >
            <textarea
                data-testid="output-whatsapp-preview"
                readOnly
                value={ready ? text : ""}
                placeholder={ready ? "· loading preview" : "approve to preview"}
                style={{
                    width: "100%", minHeight: 110, marginBottom: 8,
                    padding: "8px 10px", borderRadius: 10,
                    border: "1px solid var(--t-line2)",
                    background: "var(--t-paper)",
                    fontFamily: "var(--t-mono)",
                    fontSize: 11, lineHeight: 1.45,
                    color: "var(--t-ink2)",
                    resize: "vertical",
                }}
            />
            <div className="flex items-center gap-2 flex-wrap mb-2">
                <button
                    type="button"
                    data-testid="output-whatsapp-copy"
                    onClick={copy}
                    disabled={!ready || !text}
                    style={{
                        padding: "6px 12px", borderRadius: 999,
                        background: ready && text ? "var(--t-ink)" : "var(--t-line2)",
                        color: ready && text ? "var(--t-paper2)" : "var(--t-ink3)",
                        border: 0,
                        fontSize: 11, fontWeight: 600,
                        cursor: ready && text ? "pointer" : "not-allowed",
                    }}
                >
                    {copied ? "✓ copied" : "Copy text"}
                </button>
                <button
                    type="button"
                    data-testid="output-whatsapp-refresh"
                    onClick={previewText}
                    disabled={!ready || loading}
                    style={{
                        padding: "6px 12px", borderRadius: 999,
                        background: "transparent",
                        color: "var(--t-ink2)",
                        border: "1px solid var(--t-ink2)",
                        fontSize: 11, fontWeight: 600,
                        cursor: ready && !loading ? "pointer" : "not-allowed",
                    }}
                >
                    ↻ refresh
                </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <input
                    data-testid="output-whatsapp-phone"
                    type="tel"
                    placeholder="+91 988 600 0988"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={!ready}
                    style={{
                        flex: 1, minWidth: 0,
                        padding: "8px 10px", borderRadius: 10,
                        border: "1px solid var(--t-line2)",
                        background: "var(--t-paper)",
                        fontSize: 12,
                    }}
                />
                <button
                    type="button"
                    data-testid="output-whatsapp-send"
                    onClick={send}
                    disabled={!ready || !phone || loading}
                    style={{
                        padding: "8px 14px", borderRadius: 10,
                        background: ready && phone
                            ? "var(--t-ink)" : "var(--t-line2)",
                        color: ready && phone ? "var(--t-paper2)" : "var(--t-ink3)",
                        border: 0, fontSize: 11, fontWeight: 700,
                        cursor: ready && phone && !loading ? "pointer" : "not-allowed",
                        letterSpacing: "0.02em",
                    }}
                >
                    {loading ? "·…" : "Send"}
                </button>
            </div>
            {sendState === "sent" ? (
                <p
                    data-testid="output-whatsapp-sent"
                    className="t-mono"
                    style={{
                        fontSize: 10, marginTop: 6,
                        color: "var(--t-verified)",
                    }}
                >
                    ✓ sent · Gupshup accepted
                </p>
            ) : sendState && sendState.error ? (
                <p
                    data-testid="output-whatsapp-error"
                    className="t-mono"
                    style={{ fontSize: 10, marginTop: 6, color: "var(--t-ink3)" }}
                >
                    · {sendState.error}
                </p>
            ) : null}
        </OutputCard>
    );
}


function CollectionCard({ draft, ready }) {
    const [json, setJson] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!ready || !draft?.draft_id) return;
        setLoading(true);
        try {
            const res = await fetchCurationCollection(draft.draft_id);
            setJson(res);
        } catch (e) {
            setJson({ error: e?.message || "failed" });
        } finally {
            setLoading(false);
        }
    }, [draft?.draft_id, ready]);

    useEffect(() => { if (ready) load(); }, [ready, load]);

    return (
        <OutputCard
            testId="output-collection"
            label="In-app collection"
            sub="JSON · live drawer re-resolves each place_id"
        >
            <pre
                data-testid="output-collection-json"
                style={{
                    background: "var(--t-paper)",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 10,
                    padding: "8px 10px",
                    fontSize: 10, lineHeight: 1.4,
                    color: "var(--t-ink2)",
                    fontFamily: "var(--t-mono)",
                    maxHeight: 200, overflow: "auto",
                    whiteSpace: "pre-wrap", wordBreak: "break-all",
                    margin: 0,
                }}
            >
                {ready
                    ? (loading ? "· loading…" : JSON.stringify(json, null, 2))
                    : "approve to load"}
            </pre>
            <button
                type="button"
                data-testid="output-collection-refresh"
                onClick={load}
                disabled={!ready || loading}
                style={{
                    marginTop: 8,
                    padding: "6px 12px", borderRadius: 999,
                    background: "transparent",
                    color: "var(--t-ink3)",
                    border: "1px solid var(--t-line2)",
                    fontSize: 11, fontWeight: 600,
                    cursor: ready && !loading ? "pointer" : "not-allowed",
                }}
            >
                ↻ refresh
            </button>
        </OutputCard>
    );
}


function OutputCard({ testId, label, sub, children }) {
    return (
        <div
            data-testid={testId}
            style={{
                background: "var(--t-paper)",
                border: "1px solid var(--t-line2)",
                borderRadius: 12,
                padding: "14px 16px",
                display: "flex", flexDirection: "column",
                minHeight: 200,
            }}
        >
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 9.5, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                {label}
            </div>
            <div
                className="t-serif"
                style={{
                    fontSize: 14, lineHeight: 1.25, marginTop: 2,
                    color: "var(--t-ink)", marginBottom: 10,
                    fontStyle: "italic",
                }}
            >
                {sub}
            </div>
            <div style={{ marginTop: "auto" }}>
                {children}
            </div>
        </div>
    );
}


// Headless Chrome (and any iframe without user-gesture) often rejects
// `navigator.clipboard.writeText` silently. Fall back to the legacy
// `document.execCommand("copy")` so the founder always gets feedback.
async function safeClipboardWrite(text) {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // fall through to legacy path
    }
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-1000px";
        ta.style.top = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch {
        return false;
    }
}




// ── Magic Curation Phase B4 · brief-driven flow + mode toggle ────────
//
// Founder ruling 5 Jun 2026 night (TABLE_MAGIC_CURATION.md §0, §9):
//   "Brief-driven is the default and the front door. Pick-first
//    stays as a 'manual mode' toggle · demote, don't delete."
//
// The brief-driven view is intentionally just three things:
//   1. brief textarea  ("5–8 June, vegetarian for Mr Dhariwal …")
//   2. an honest "thinking" panel that shows the parsed brief +
//      the queries the pipeline is running
//   3. the discovered slate · founder reviews + clicks "Compose"
//      → falls into the existing compose() → draft → approve →
//      four outputs flow
//
// DOCTRINE (the founder's burned-in line):
//   "Brief in → real searches run → only real results arrive →
//    model arranges and writes around them."
// The model NEVER proposes a venue. The brief parser emits queries
// (category-shaped, name-stripped); discovery runs them through
// the v2 search_venues pipeline; we hand the verified slate to the
// model for composition only. Same firewall, one level up.

function ModeToggle({ mode, setMode }) {
    return (
        <div
            data-testid="curate-mode-toggle"
            className="flex items-center justify-center gap-2 mb-6"
        >
            <div
                style={{
                    display: "inline-flex",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 999,
                    background: "var(--t-paper2)",
                    padding: 3,
                }}
            >
                <button
                    type="button"
                    data-testid="curate-mode-brief"
                    onClick={() => setMode("brief")}
                    style={{
                        padding: "7px 18px",
                        fontSize: 12, fontWeight: 700,
                        letterSpacing: "0.04em",
                        background: mode === "brief"
                            ? "var(--t-ink)" : "transparent",
                        color: mode === "brief"
                            ? "var(--t-paper2)" : "var(--t-ink2)",
                        border: 0, borderRadius: 999,
                        cursor: "pointer",
                    }}
                >
                    Brief mode
                </button>
                <button
                    type="button"
                    data-testid="curate-mode-manual"
                    onClick={() => setMode("manual")}
                    style={{
                        padding: "7px 18px",
                        fontSize: 12, fontWeight: 700,
                        letterSpacing: "0.04em",
                        background: mode === "manual"
                            ? "var(--t-ink)" : "transparent",
                        color: mode === "manual"
                            ? "var(--t-paper2)" : "var(--t-ink2)",
                        border: 0, borderRadius: 999,
                        cursor: "pointer",
                    }}
                >
                    Manual mode
                </button>
            </div>
            <span
                className="t-mono uppercase"
                style={{
                    fontSize: 9, letterSpacing: "0.18em",
                    color: "var(--t-ink3)", marginLeft: 8,
                    fontStyle: "italic",
                }}
            >
                {mode === "brief"
                    ? "· write the brief · we discover"
                    : "· you already know the venues"}
            </span>
        </div>
    );
}


function BriefDrivenView({ onComposed, composing, onOpenDetail }) {
    const [brief, setBrief] = useState("");
    const [phase, setPhase] = useState("write");   // write · parsing · discovering · slate · error
    const [parsed, setParsed] = useState(null);
    const [discovery, setDiscovery] = useState(null);
    const [err, setErr] = useState(null);
    // P0 · tick-to-select on the slate (§15 of TABLE_CURATED_ANCHOR_LAYER.md).
    // Ordered list — tick order IS the default compose sequence. A new
    // brief clears the selection (state is per-slate, not global).
    const [selectedIds, setSelectedIds] = useState([]);

    const toggleSelected = useCallback((venueId) => {
        if (!venueId) return;
        setSelectedIds((cur) => (
            cur.includes(venueId)
                ? cur.filter((id) => id !== venueId)
                : [...cur, venueId]
        ));
    }, []);

    const run = useCallback(async () => {
        const trimmed = brief.trim();
        if (trimmed.length < 8) {
            setErr("Brief is too short — give us a sentence or two.");
            return;
        }
        setErr(null);
        setPhase("parsing");
        // New brief → new selection. Doctrine: "Selected state persists
        // across the founder's pan/zoom inside the slate; clears on a
        // new brief." (§15.6)
        setSelectedIds([]);
        try {
            const p = await parseCurationBrief(trimmed);
            if (p.error) {
                setErr(p.error);
                setPhase("error");
                return;
            }
            setParsed(p);
            setPhase("discovering");
            const d = await discoverFromBrief(p, {
                per_query_limit: 8, slate_cap: 12,
            });
            setDiscovery(d);
            setPhase("slate");
        } catch (e) {
            setErr(e?.message || "Discovery failed.");
            setPhase("error");
        }
    }, [brief]);

    const composeFromSlate = () => {
        // P0 · curate-down before compose (§15).
        // Tick-order is the default sequence. Empty selection falls
        // back to ALL venues — preserves today's compose-all behaviour
        // for users who don't tick (§15.5).
        const fullSlate = discovery?.venues || [];
        if (fullSlate.length === 0) {
            setErr("No verified venues to compose from.");
            return;
        }
        let slate;
        if (selectedIds.length === 0) {
            slate = fullSlate;
        } else {
            // Preserve tick order · build a map and follow selectedIds.
            const byId = new Map();
            for (const v of fullSlate) {
                const vid = v.id || v.place_id;
                if (vid) byId.set(vid, v);
            }
            slate = selectedIds
                .map((id) => byId.get(id))
                .filter(Boolean);
            // Safety net · if no selected ids matched the slate (e.g.
            // stale selection across a re-fetch), fall back to full.
            if (slate.length === 0) slate = fullSlate;
        }
        const occasion = parsed?.occasion || "";
        onComposed(slate, brief.trim(), occasion);
    };

    return (
        <section data-testid="curate-brief-view">
            <h1
                className="t-serif text-center"
                style={{
                    fontSize: 30, fontWeight: 500,
                    color: "var(--t-ink)", lineHeight: 1.1,
                    letterSpacing: "-0.01em",
                    margin: "0 0 6px",
                }}
            >
                Write the brief.
            </h1>
            <p
                className="t-serif italic text-center"
                style={{
                    fontSize: 14, color: "var(--t-ink3)",
                    margin: "0 0 24px",
                }}
            >
                We discover the real venues. The model arranges. Nothing
                from memory.
            </p>

            <textarea
                data-testid="curate-brief-input"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                disabled={phase === "parsing" || phase === "discovering" || composing}
                placeholder={
                    "e.g. 5–8 June, vegetarian fine dining for Mr Aditya "
                    + "Dhariwal, party of 5, in Madrid. Include some "
                    + "Indian too — it's a comfort fallback."
                }
                style={{
                    width: "100%", minHeight: 160,
                    padding: "14px 16px",
                    borderRadius: 14,
                    border: "1.5px solid var(--t-line2)",
                    background: "var(--t-paper2)",
                    fontFamily: "'Source Serif Pro', Georgia, serif",
                    fontSize: 15, lineHeight: 1.5,
                    color: "var(--t-ink)",
                    resize: "vertical",
                }}
            />

            <div className="flex items-center gap-3 mt-3 flex-wrap">
                <button
                    type="button"
                    data-testid="curate-brief-run"
                    onClick={run}
                    disabled={phase === "parsing" || phase === "discovering"
                              || composing || brief.trim().length < 8}
                    style={{
                        padding: "12px 22px",
                        borderRadius: 999,
                        background: brief.trim().length >= 8
                            && phase !== "parsing" && phase !== "discovering"
                            && !composing
                            ? "var(--t-ink)" : "var(--t-line2)",
                        color: "var(--t-paper2)",
                        border: 0,
                        fontSize: 13, fontWeight: 700,
                        letterSpacing: "0.02em",
                        cursor: phase === "parsing" || phase === "discovering"
                                || composing ? "wait" : "pointer",
                    }}
                >
                    {phase === "parsing" ? "· parsing brief…"
                        : phase === "discovering" ? "· discovering venues…"
                        : composing ? "· composing…"
                        : "Run discovery"}
                </button>
            </div>

            {err ? (
                <div
                    data-testid="curate-brief-error"
                    style={{
                        marginTop: 18,
                        padding: "12px 16px",
                        background: "var(--t-paper2)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 12,
                        color: "var(--t-ink2)",
                        fontSize: 13,
                    }}
                >
                    <span
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            color: "var(--t-brass)",
                        }}
                    >
                        · couldn't read the brief
                    </span>{" "}
                    {err}
                </div>
            ) : null}

            {phase === "parsing" || phase === "discovering"
             || (phase === "slate" && discovery) ? (
                <ThinkingPanel
                    parsed={parsed}
                    discovery={discovery}
                    phase={phase}
                />
            ) : null}

            {phase === "slate" && discovery ? (
                <DiscoveredSlate
                    venues={discovery.venues}
                    legs={discovery.legs || []}
                    briefCities={discovery.brief_cities || []}
                    selectedIds={selectedIds}
                    onToggleSelected={toggleSelected}
                    onOpenDetail={onOpenDetail}
                />
            ) : null}

            {phase === "slate" && discovery && (discovery.venues?.length || 0) > 0 ? (
                <ComposeCTAFooter
                    onCompose={composeFromSlate}
                    composing={composing}
                    venueCount={discovery.venues.length}
                    selectedCount={selectedIds.length}
                />
            ) : null}
        </section>
    );
}


function ComposeCTAFooter({ onCompose, composing, venueCount, selectedCount = 0 }) {
    // P0 · tick-to-select on the slate (§15 of TABLE_CURATED_ANCHOR_LAYER.md).
    // Selection IS approval at the slate stage · today's compose-all is
    // implicit approval of every candidate, which is wrong for an
    // itinerary. The counter strip + adaptive button copy makes the
    // founder's choice visible before compose fires.
    const hasSelection = selectedCount > 0;
    const composeLabel = composing
        ? "· composing\u2026"
        : hasSelection
            ? `Compose selected ${selectedCount} \u2192`
            : "Compose all \u2192";
    return (
        <section
            data-testid="curate-brief-compose-footer"
            style={{
                marginTop: 36,
                padding: "28px 24px",
                background: "var(--t-paper2)",
                border: "1px solid var(--t-brass)",
                borderRadius: 16,
                textAlign: "center",
            }}
        >
            <div
                className="t-mono uppercase"
                data-testid="curate-brief-selected-counter"
                style={{
                    fontSize: 10, letterSpacing: "0.22em",
                    color: hasSelection ? "var(--t-verified)" : "var(--t-brass)",
                    marginBottom: 10,
                }}
            >
                {hasSelection
                    ? `\u00b7 ${selectedCount} of ${venueCount} selected`
                    : "\u00b7 none ticked \u00b7 will compose all"}
            </div>
            <h3
                className="t-serif"
                style={{
                    fontSize: 22, fontWeight: 500,
                    color: "var(--t-ink)",
                    lineHeight: 1.2,
                    margin: "0 0 8px",
                }}
            >
                {hasSelection
                    ? `${selectedCount} venue${selectedCount === 1 ? "" : "s"} ticked from ${venueCount} on the slate.`
                    : `${venueCount} verified venue${venueCount === 1 ? "" : "s"} on the slate.`}
            </h3>
            <p
                className="t-serif italic"
                style={{
                    fontSize: 14, color: "var(--t-ink3)",
                    margin: "0 0 18px",
                }}
            >
                {hasSelection
                    ? "Tick-order is the sequence \u00b7 the model arranges \u00b7 you approve."
                    : "The pipeline owns the truth. The model arranges. The founder approves."}
            </p>
            <button
                type="button"
                data-testid="curate-brief-compose"
                onClick={onCompose}
                disabled={composing}
                style={{
                    padding: "14px 32px",
                    borderRadius: 999,
                    background: composing ? "var(--t-line2)" : "var(--t-ink)",
                    color: composing ? "var(--t-ink3)" : "var(--t-paper2)",
                    border: 0,
                    fontSize: 14, fontWeight: 700,
                    letterSpacing: "0.02em",
                    cursor: composing ? "wait" : "pointer",
                    boxShadow: composing
                        ? "none"
                        : "0 4px 16px rgba(26, 34, 56, 0.25)",
                }}
            >
                {composeLabel}
            </button>
        </section>
    );
}


function ThinkingPanel({ parsed, discovery, phase }) {
    return (
        <section
            data-testid="curate-brief-thinking"
            style={{
                marginTop: 28,
                padding: "18px 20px",
                background: "var(--t-paper2)",
                border: "1px solid var(--t-line2)",
                borderRadius: 14,
            }}
        >
            <div
                className="t-mono uppercase mb-3"
                style={{
                    fontSize: 10, letterSpacing: "0.22em",
                    color: "var(--t-brass)",
                }}
            >
                · thinking out loud
            </div>
            <ThinkingStep
                label="read the brief"
                done={!!parsed}
                detail={parsed
                    ? `${parsed.destination?.city || "—"} · ${parsed.occasion || "no occasion stated"} · ${(parsed.dietary || []).join(", ") || "no dietary"}`
                    : "parsing your words…"
                }
            />
            <ThinkingStep
                label="derive queries"
                done={!!parsed?.derived_queries?.length}
                detail={parsed?.derived_queries?.length
                    ? parsed.derived_queries.join(" · ")
                    : "—"
                }
            />
            <ThinkingStep
                label="discover venues (v2 pipeline)"
                done={!!discovery?.venues?.length}
                detail={discovery
                    ? `${discovery.total_seen} unique venues · ${discovery.queries_run?.length || 0} queries run`
                    : phase === "discovering"
                        ? "fan-out in flight…"
                        : "—"
                }
            />
            {discovery?.dropped_dietary?.length ? (
                <ThinkingStep
                    label="honest exclusions"
                    done={true}
                    detail={
                        `dropped ${discovery.dropped_dietary.length} venue(s) `
                        + `that contradict your dietary — `
                        + discovery.dropped_dietary.slice(0, 5).join(", ")
                    }
                    accent="brass"
                />
            ) : null}
        </section>
    );
}


function ThinkingStep({ label, done, detail, accent = "ink" }) {
    const dotColor = done
        ? (accent === "brass" ? "var(--t-brass)" : "var(--t-verified)")
        : "var(--t-line2)";
    return (
        <div
            className="flex items-start gap-3 mb-2"
            data-testid={`curate-think-${label.replace(/\s+/g, "-")}`}
        >
            <span
                style={{
                    display: "inline-block",
                    width: 8, height: 8, borderRadius: 999,
                    background: dotColor,
                    marginTop: 7,
                    flexShrink: 0,
                }}
            />
            <div style={{ flex: 1 }}>
                <div
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.16em",
                        color: done ? "var(--t-ink)" : "var(--t-ink3)",
                    }}
                >
                    {label}
                </div>
                <div
                    style={{
                        fontSize: 12,
                        color: done ? "var(--t-ink2)" : "var(--t-ink3)",
                        fontStyle: done ? "normal" : "italic",
                        marginTop: 1,
                    }}
                >
                    {detail}
                </div>
            </div>
        </div>
    );
}


function DiscoveredSlate({ venues, legs, briefCities, selectedIds = [], onToggleSelected, onOpenDetail }) {
    if (!venues || venues.length === 0) {
        return (
            <div
                data-testid="curate-brief-slate-empty"
                className="t-serif italic text-center"
                style={{
                    fontSize: 14, color: "var(--t-ink3)",
                    padding: 30,
                }}
            >
                We searched the pipeline · came back honest-blank. Try
                widening the brief or switch to manual mode.
            </div>
        );
    }
    // Multi-city: group venues by the leg they belong to + render
    // honest "Concierge® will curate on the ground" lines for thin
    // legs (the brief asked, the pipeline returned <2 verified).
    const isMultiCity = Array.isArray(legs) && legs.length > 1;
    return (
        <section
            data-testid="curate-brief-slate"
            style={{ marginTop: 28 }}
        >
            <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.22em",
                            color: "var(--t-brass)",
                        }}
                    >
                        the slate · verified
                    </div>
                    <h2
                        className="t-serif"
                        style={{
                            fontSize: 22, fontWeight: 500,
                            color: "var(--t-ink)", marginTop: 4,
                            lineHeight: 1.2,
                        }}
                    >
                        {venues.length} venue{venues.length === 1 ? "" : "s"} from the v2 pipeline
                        {isMultiCity ? (
                            <span style={{
                                fontSize: 14, color: "var(--t-ink3)",
                                fontStyle: "italic", marginLeft: 8,
                            }}>
                                · across {legs.length} cities
                            </span>
                        ) : null}
                    </h2>
                </div>
            </div>
            {isMultiCity ? (
                <GroupedByCity
                    venues={venues}
                    legs={legs}
                    selectedIds={selectedIds}
                    onToggleSelected={onToggleSelected}
                    onOpenDetail={onOpenDetail}
                />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {venues.map((v, i) => (
                        <SlateCard
                            key={v.id || v.place_id || i}
                            venue={v}
                            index={i}
                            selected={selectedIds.includes(v.id || v.place_id)}
                            onToggle={onToggleSelected}
                            onOpenDetail={onOpenDetail}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}


function GroupedByCity({ venues, legs, selectedIds = [], onToggleSelected, onOpenDetail }) {
    // Build per-city venue list using substring match on venue.city
    // (mirrors backend `_venue_city_matches`).
    const matchVenue = (vcity, bcity) => {
        if (!vcity || !bcity) return false;
        const v = String(vcity).toLowerCase().replace(/-/g, " ");
        const b = String(bcity).toLowerCase().replace(/-/g, " ");
        const tokens = b.split(/\s+/).filter(Boolean);
        return tokens.every((t) => v.includes(t));
    };
    let runningIndex = 0;
    return (
        <div data-testid="curate-brief-slate-grouped">
            {legs.map((leg) => {
                const bucket = venues.filter((v) => matchVenue(v.city, leg.city));
                const indices = bucket.map(() => runningIndex++);
                return (
                    <div
                        key={leg.city}
                        data-testid={`curate-brief-leg-${leg.city.replace(/\s+/g, "-")}`}
                        style={{ marginBottom: 24 }}
                    >
                        <div
                            className="flex items-baseline gap-3 flex-wrap"
                            style={{
                                borderBottom: "1px solid var(--t-line2)",
                                paddingBottom: 6, marginBottom: 12,
                            }}
                        >
                            <span
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 10, letterSpacing: "0.22em",
                                    color: leg.thin ? "var(--t-brass)" : "var(--t-verified)",
                                }}
                            >
                                · {leg.city}
                            </span>
                            <span
                                style={{
                                    fontSize: 12, color: "var(--t-ink3)",
                                    fontStyle: "italic",
                                }}
                            >
                                {bucket.length} verified
                                {leg.count_found > bucket.length
                                    ? ` · ${leg.count_found} found`
                                    : ""}
                            </span>
                        </div>
                        {bucket.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {bucket.map((v, i) => (
                                    <SlateCard
                                        key={v.id || v.place_id || i}
                                        venue={v}
                                        index={indices[i]}
                                        selected={selectedIds.includes(v.id || v.place_id)}
                                        onToggle={onToggleSelected}
                                        onOpenDetail={onOpenDetail}
                                    />
                                ))}
                            </div>
                        ) : null}
                        {leg.thin ? (
                            <ThinLegLine
                                city={leg.city}
                                count={bucket.length}
                            />
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}


function ThinLegLine({ city, count }) {
    return (
        <div
            data-testid={`curate-brief-thin-${city.replace(/\s+/g, "-")}`}
            className="t-serif italic"
            style={{
                marginTop: count > 0 ? 12 : 0,
                padding: "14px 18px",
                background: "var(--t-paper2)",
                border: "1px dashed var(--t-brass)",
                borderRadius: 12,
                fontSize: 13,
                color: "var(--t-ink2)",
                lineHeight: 1.5,
            }}
        >
            {count === 0 ? (
                <>
                    The v2 pipeline returned no verified venues for{" "}
                    <strong style={{ color: "var(--t-ink)" }}>{city}</strong>.
                    {" "}Concierge® will curate this leg on the ground —
                    rather than thin-stretch the dossier.
                </>
            ) : (
                <>
                    Only {count} verified result{count === 1 ? "" : "s"} for{" "}
                    <strong style={{ color: "var(--t-ink)" }}>{city}</strong>.
                    {" "}Concierge® will curate the rest on the ground.
                </>
            )}
        </div>
    );
}


function SlateCard({ venue, index, selected = false, onToggle, onOpenDetail }) {
    const venueId = venue.id || venue.place_id;
    const canTick = typeof onToggle === "function" && Boolean(venueId);
    const canOpenDetail = typeof onOpenDetail === "function";
    const handleToggle = (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if (canTick) onToggle(venueId);
    };
    const handleOpenDetail = () => {
        if (canOpenDetail) onOpenDetail(venue);
    };
    // Richer body fields surfaced on the slate card itself
    // (founder Pic 1 ask · 11 Feb 2026 PM). All sourced from the
    // verified pipeline card · NEVER inferred.
    const cuisineTags = Array.isArray(venue.cuisine_tags) ? venue.cuisine_tags : [];
    const cuisineLine = cuisineTags.slice(0, 2).join(" · ");
    const stars = Number.isFinite(Number(venue.michelin_stars))
        ? Math.max(0, Math.min(3, Number(venue.michelin_stars)))
        : 0;
    const isBib = !stars && (
        (venue.michelin_distinction || "").toString().toLowerCase() === "bib_gourmand"
    );
    const arc = venue.archive_texture || {};
    const archiveStars = Number.isFinite(Number(arc.michelin_stars))
        ? Math.max(0, Math.min(3, Number(arc.michelin_stars)))
        : 0;
    const showStars = stars || archiveStars;
    const showBib = !showStars && (
        isBib
        || (arc.michelin_distinction || "").toString().toLowerCase() === "bib_gourmand"
    );
    // open_now is a soft signal · Places returns true/false/undefined.
    // We only render the dot when the venue gave a definitive boolean.
    const openNow = typeof venue.open_now === "boolean" ? venue.open_now : null;
    return (
        <div
            data-testid={`curate-brief-slate-card-${index}`}
            data-selected={selected ? "true" : "false"}
            onClick={canOpenDetail ? handleOpenDetail : handleToggle}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (canOpenDetail) handleOpenDetail();
                    else handleToggle();
                }
            }}
            role={canOpenDetail ? "button" : (canTick ? "checkbox" : undefined)}
            aria-checked={canTick && !canOpenDetail ? selected : undefined}
            aria-pressed={canOpenDetail ? selected : undefined}
            aria-label={canOpenDetail
                ? `View details for ${venue.name || "venue"}`
                : undefined}
            tabIndex={canTick || canOpenDetail ? 0 : undefined}
            style={{
                background: selected ? "var(--t-ink)" : "var(--t-paper2)",
                color: selected ? "var(--t-paper2)" : undefined,
                border: selected
                    ? "1px solid var(--t-ink)"
                    : "1px solid var(--t-line2)",
                borderRadius: 12,
                padding: "14px 16px",
                cursor: (canTick || canOpenDetail) ? "pointer" : "default",
                transition: "background-color 140ms ease, border-color 140ms ease, color 140ms ease",
                boxShadow: selected ? "0 6px 18px rgba(26, 34, 56, 0.18)" : "none",
                position: "relative",
            }}
        >
            <div className="flex items-start gap-2">
                {canTick ? (
                    // Tick is its own click target · toggling MUST stop
                    // propagation so it doesn't also open the drawer.
                    <button
                        type="button"
                        data-testid={`slate-tick-${index}`}
                        onClick={handleToggle}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                handleToggle();
                            }
                        }}
                        aria-label={selected
                            ? `Deselect ${venue.name || "venue"}`
                            : `Select ${venue.name || "venue"}`}
                        aria-pressed={selected}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 22, height: 22,
                            borderRadius: 999,
                            marginTop: -1,
                            border: selected
                                ? "1px solid var(--t-paper2)"
                                : "1px solid var(--t-brass)",
                            background: selected ? "var(--t-paper2)" : "transparent",
                            color: selected ? "var(--t-ink)" : "var(--t-brass)",
                            fontSize: 12,
                            fontWeight: 800,
                            lineHeight: 1,
                            flexShrink: 0,
                            cursor: "pointer",
                            padding: 0,
                        }}
                    >
                        {selected ? "\u2713" : ""}
                    </button>
                ) : null}
                <span
                    className="t-mono"
                    style={{
                        fontSize: 11,
                        color: selected ? "var(--t-paper2)" : "var(--t-brass)",
                        fontWeight: 700,
                        marginTop: 2,
                    }}
                >
                    {String(index + 1).padStart(2, "0")}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        className="t-serif"
                        style={{
                            fontSize: 15,
                            color: selected ? "var(--t-paper2)" : "var(--t-ink)",
                            fontWeight: 600,
                            lineHeight: 1.2,
                        }}
                    >
                        {venue.name}
                        {showStars ? (
                            <span
                                data-testid={`slate-michelin-stars-${index}`}
                                aria-label={`${showStars} Michelin star${showStars === 1 ? "" : "s"}`}
                                style={{
                                    marginLeft: 6,
                                    fontSize: 11,
                                    color: selected ? "var(--t-paper2)" : "var(--t-brass)",
                                    letterSpacing: "0.05em",
                                }}
                            >
                                {"\u2605".repeat(showStars)}
                            </span>
                        ) : null}
                        {showBib ? (
                            <span
                                data-testid={`slate-michelin-bib-${index}`}
                                className="t-mono uppercase"
                                style={{
                                    marginLeft: 6,
                                    fontSize: 9,
                                    color: selected ? "var(--t-paper2)" : "var(--t-brass)",
                                    border: selected
                                        ? "1px solid var(--t-paper2)"
                                        : "1px solid var(--t-brass)",
                                    padding: "1px 5px",
                                    borderRadius: 4,
                                    letterSpacing: "0.10em",
                                    verticalAlign: "middle",
                                }}
                            >
                                Bib
                            </span>
                        ) : null}
                    </div>
                    {venue.city ? (
                        <div
                            className="t-mono"
                            style={{
                                fontSize: 10,
                                color: selected ? "var(--t-line2)" : "var(--t-ink3)",
                                letterSpacing: "0.04em",
                                marginTop: 2,
                            }}
                        >
                            {venue.city}
                        </div>
                    ) : null}
                    {cuisineLine ? (
                        <div
                            data-testid={`slate-cuisine-${index}`}
                            className="t-serif"
                            style={{
                                fontSize: 12,
                                color: selected ? "var(--t-paper2)" : "var(--t-ink2)",
                                fontStyle: "italic",
                                marginTop: 4,
                                lineHeight: 1.3,
                            }}
                        >
                            {cuisineLine}
                        </div>
                    ) : null}
                </div>
                {canOpenDetail ? (
                    // Quiet "▸ details" affordance so the curator sees
                    // the card body is clickable separately from the
                    // tick. Shows up on hover on a touch device this
                    // stays visible (no hover state).
                    <span
                        aria-hidden="true"
                        data-testid={`slate-details-affordance-${index}`}
                        className="t-mono uppercase"
                        style={{
                            fontSize: 9,
                            color: selected ? "var(--t-line2)" : "var(--t-ink3)",
                            letterSpacing: "0.18em",
                            marginTop: 2,
                            flexShrink: 0,
                            opacity: 0.7,
                        }}
                    >
                        details ↗
                    </span>
                ) : null}
            </div>
            <div
                className="flex items-center gap-2 mt-2 flex-wrap"
                style={{
                    fontSize: 11,
                    color: selected ? "var(--t-line2)" : "var(--t-ink2)",
                }}
            >
                {venue.rating ? (
                    <span data-testid={`slate-rating-${index}`}>
                        ★ {venue.rating}
                        {venue.user_ratings_total
                            ? ` (${venue.user_ratings_total.toLocaleString()})`
                            : ""}
                    </span>
                ) : null}
                {openNow !== null ? (
                    <span
                        data-testid={`slate-open-now-${index}`}
                        className="t-mono uppercase"
                        style={{
                            fontSize: 9,
                            letterSpacing: "0.10em",
                            color: openNow
                                ? (selected ? "var(--t-paper2)" : "var(--t-verified)")
                                : (selected ? "var(--t-line2)" : "var(--t-ink3)"),
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                display: "inline-block",
                                width: 6, height: 6, borderRadius: 999,
                                background: openNow
                                    ? (selected ? "var(--t-paper2)" : "var(--t-verified)")
                                    : (selected ? "var(--t-line2)" : "var(--t-ink3)"),
                            }}
                        />
                        {openNow ? "open" : "closed"}
                    </span>
                ) : null}
                {venue.route_via ? (
                    <span
                        className="t-mono"
                        style={{
                            fontSize: 9,
                            color: selected ? "var(--t-paper2)" : "var(--t-brass)",
                            letterSpacing: "0.06em",
                            background: selected
                                ? "rgba(255, 255, 255, 0.12)"
                                : "var(--t-brass-wash)",
                            padding: "2px 8px",
                            borderRadius: 999,
                        }}
                    >
                        {venue.route_via}
                    </span>
                ) : null}
            </div>
        </div>
    );
}
